import React, { useEffect, useState } from "react";
import { format } from "date-fns"; // ✅ Added for cleaner formatting

const AllTasksSection = ({ BASE_URL }) => {
  const [tasks, setTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTaskPage, setCurrentTaskPage] = useState(1);
  const tasksPerPage = 10;
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editedDescription, setEditedDescription] = useState("");

  useEffect(() => {
    const fetchAllTasks = async () => {
      setLoading(true);
      setError(null);
      try {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        // ✅ Use DD-MM-YYYY to match backend
        const start = format(yesterday, "dd-MM-yyyy");
        const end = format(today, "dd-MM-yyyy");

        const res = await fetch(`${BASE_URL}/admin/tasks-range?start=${start}&end=${end}`);
        if (!res.ok) throw new Error("Failed to fetch all tasks");
        const data = await res.json();
        setTasks(data);
      } catch (err) {
        console.error("Error fetching all tasks:", err);
        setError("Failed to fetch tasks");
      } finally {
        setLoading(false);
      }
    };
    fetchAllTasks();
  }, [BASE_URL]);

  const filtered = tasks.filter((t) =>
    [t.developer_name, t.project_name, t.task_type, t.date, t.status, t.description]
      .some((f) => String(f || "").toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filtered.length / tasksPerPage);
  const currentTasks = filtered.slice(
    (currentTaskPage - 1) * tasksPerPage,
    currentTaskPage * tasksPerPage
  );

  const handleEditClick = (task) => {
    setEditingTaskId(task.id);
    setEditedDescription(task.description);
  };

  const handleSaveEdit = async (taskId) => {
    try {
      const res = await fetch(`${BASE_URL}/admin/update-task/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description: editedDescription }),
      });
      if (!res.ok) throw new Error("Failed to update task");
      const updated = await res.json();
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, description: updated.description } : t))
      );
      setEditingTaskId(null);
    } catch (err) {
      console.error("Error saving task edit:", err);
    }
  };

  return (
    <div className="bg-gray-800 p-6 rounded-2xl shadow-lg overflow-x-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">All Tasks (Today + Yesterday)</h2>
        <input
          type="text"
          placeholder="🔍 Search tasks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="bg-gray-700 text-white px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-64"
        />
      </div>

      {loading ? (
        <p className="text-gray-400 text-center">Loading tasks...</p>
      ) : error ? (
        <p className="text-red-500 text-center">{error}</p>
      ) : (
        <table className="min-w-full border border-gray-700 table-fixed">
          <thead>
            <tr className="bg-gray-700 text-gray-200">
              <th className="py-2 px-4 border border-gray-600 min-w-[150px]">Developer Name</th>
              <th className="py-2 px-4 border border-gray-600 min-w-[130px]">Task Type</th>
              <th className="py-2 px-4 border border-gray-600 min-w-[150px]">Project</th>
              <th className="py-2 px-4 border border-gray-600">Date</th>
              <th className="py-2 px-4 border border-gray-600">Time Slot</th>
              <th className="py-2 px-4 border border-gray-600 min-w-[250px]">Description</th>
              <th className="py-2 px-4 border border-gray-600">Status</th>
            </tr>
          </thead>
          <tbody>
            {currentTasks.length > 0 ? (
              currentTasks.map((task, idx) => (
                <tr key={idx} className="text-gray-300 text-sm">
                  <td className="py-2 px-4 border border-gray-700">{task.developer_name}</td>
                  <td className="py-2 px-4 border border-gray-700">{task.task_type}</td>
                  <td className="py-2 px-4 border border-gray-700">{task.project_name}</td>
                  <td className="py-2 px-4 border border-gray-700">{task.date}</td>
                  <td className="py-2 px-4 border border-gray-700">{task.time_slot}</td>
                  <td className="py-2 px-4 border border-gray-700">
                    {editingTaskId === task.id ? (
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          className="bg-gray-700 text-white px-2 py-1 rounded w-full"
                        />
                        <button
                          onClick={() => handleSaveEdit(task.id)}
                          className="bg-green-600 px-2 py-1 rounded text-sm"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingTaskId(null)}
                          className="bg-gray-600 px-2 py-1 rounded text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div>
                        {task.description}
                        <button
                          onClick={() => handleEditClick(task)}
                          className="ml-2 bg-blue-600 px-2 py-1 rounded text-xs hover:bg-blue-700"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </td>
                  <td
                    className={`py-2 px-4 border border-gray-700 font-semibold ${
                      task.status === "Completed" ? "text-green-500" : "text-yellow-400"
                    }`}
                  >
                    {task.status}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center text-gray-500 py-4 border border-gray-700">
                  No tasks found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-4 space-x-2">
          <button
            disabled={currentTaskPage === 1}
            onClick={() => setCurrentTaskPage((p) => p - 1)}
            className="px-3 py-1 bg-gray-700 rounded-md disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-gray-300">
            Page {currentTaskPage} of {totalPages}
          </span>
          <button
            disabled={currentTaskPage === totalPages}
            onClick={() => setCurrentTaskPage((p) => p + 1)}
            className="px-3 py-1 bg-gray-700 rounded-md disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AllTasksSection;
